import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikErrors, FormikTouched } from 'formik';
import { IoMdMail } from 'react-icons/io';
import { Turnstile } from '@marsidev/react-turnstile';

import { isProduction } from '@/settings/environment';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';
import { TurnstileHelpMessage } from '@/components/shared/TurnstileHelpMessage';

import * as styles from './index.module.scss';

export type Values = {
  email: string;
  ignoreTurnstile: boolean;
};

type Props = {
  errors: FormikErrors<Values>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isDisabled: boolean;
  touched: FormikTouched<Values>;
  values: Values;
  // Turnstile 関連
  turnstileError: string;
  turnstileLoading: boolean;
  onTurnstileSuccess: (token: string) => void;
  onTurnstileError: () => void;
};
export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  isDisabled,
  touched,
  values,
  turnstileError,
  turnstileLoading,
  onTurnstileSuccess,
  onTurnstileError,
}) => {
  const { t, i18n } = useTranslation();

  // ボタンの非活性条件を変数として抽出
  // Turnstileローディング中（スキップしていない場合）またはフォームエラー時に非活性
  const isSubmitDisabled =
    (turnstileLoading && !values.ignoreTurnstile) || isDisabled;

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="email">
          {t('ui.email.label')}
        </label>
        <div className={classnames(styles.inputWrapper, styles.iconLeft)}>
          <IoMdMail className={styles.icon} />
          <input
            id="email"
            data-test="email-field"
            className={classnames(styles.input, {
              [styles.error]: errors.email && touched.email,
            })}
            name="email"
            type="email"
            onChange={handleChange}
            placeholder={t('ui.email.placeholder')}
            value={values.email}
          />
        </div>
        {touched.email && errors.email && (
          <div>
            <Label>{errors.email}</Label>
          </div>
        )}
      </div>

      <div className={styles.turnstileWrapper}>
        <Turnstile
          siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
          options={{
            language: i18n.language,
            theme: 'light',
          }}
          onSuccess={onTurnstileSuccess}
          onError={onTurnstileError}
        />
        {turnstileError && (
          <div className={styles.turnstileError} data-test="turnstile-error">
            <Label>{turnstileError}</Label>
          </div>
        )}
        {/* 開発環境のみスキップチェックボックスを表示 */}
        {!isProduction() && (
          <label className={styles.checkbox} htmlFor="ignoreTurnstile">
            <input
              type="checkbox"
              id="ignoreTurnstile"
              name="ignoreTurnstile"
              data-test="ignore-turnstile"
              checked={values.ignoreTurnstile}
              onChange={handleChange}
            />
            <span className={styles.checkboxBox} />
            <span className={styles.checkboxLabel}>
              {t('ui.turnstile.ignore')}
            </span>
          </label>
        )}
      </div>

      <div className="submit-button">
        <SharedButton
          dataTest="do-verify"
          disabled={isSubmitDisabled}
          type="submit"
        >
          {t('ui.action.register')}
        </SharedButton>
      </div>

      {/* ヘルプメッセージ: ボタンが有効にならない場合に表示 */}
      {isSubmitDisabled && <TurnstileHelpMessage />}
    </form>
  );
};
